import React from "react";
import { Briefcase, DollarSign, Users } from "lucide-react";
import SearchableSelect from "../../ui/SearchableSelect";
import Toggle from "../../ui/Toggle";

const PRACTICE_AREAS = [
  { value: "real_estate", label: "Real Estate" },
  { value: "family", label: "Family" },
  { value: "litigation", label: "Litigation" },
  { value: "criminal", label: "Criminal" },
  { value: "corporate", label: "Corporate" },
  { value: "immigration", label: "Immigration" },
  { value: "wills_estates", label: "Wills & Estates" },
  { value: "other", label: "Other" },
];

const BILLING_TYPES = [
  { value: "hourly", label: "Hourly" },
  { value: "flat", label: "Flat Fee" },
  { value: "contingency", label: "Contingency" },
  { value: "mixed", label: "Mixed" },
];

interface WizardData {
  [key: string]: any;
  createMatter: boolean;
  matterTitle: string;
  practiceArea: string;
  billingType: string;
  flatFeeCents: string;
  rateTableId: string | null;
  responsibleLawyerId: string;
  assignedUserIds: string[];
  matterConflictSameAsClient: boolean;
  matterConflictStatus: string;
  matterConflictNotes: string;
}

interface TeamUser {
  _id: string;
  name: string;
  email: string;
}

interface RateTable {
  _id: string;
  name: string;
}

interface MatterSetupStepProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  errors: Record<string, string>;
  teamUsers: TeamUser[];
  rateTables: RateTable[];
}

const MatterSetupStep: React.FC<MatterSetupStepProps> = ({
  data,
  onChange,
  errors,
  teamUsers,
  rateTables,
}) => {
  return (
    <div className="space-y-5">
      {/* Create Matter Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
        <div className="flex items-center gap-3">
          <Briefcase className="h-5 w-5 text-slate-600" />
          <div>
            <span className="text-sm font-semibold text-slate-800">Create a Matter</span>
            <p className="text-xs text-slate-500">Set up a legal matter for this client</p>
          </div>
        </div>
        <Toggle
          checked={data.createMatter}
          onChange={(val) => onChange({ createMatter: val })}
        />
      </div>

      {data.createMatter && (
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Matter Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Matter Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.matterTitle}
              onChange={(e) => onChange({ matterTitle: e.target.value })}
              className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 ${
                errors.matterTitle ? "border-red-300" : "border-slate-200"
              }`}
              placeholder="e.g. Smith Residential Purchase"
            />
            {errors.matterTitle && <p className="text-xs text-red-500 mt-1">{errors.matterTitle}</p>}
          </div>

          {/* Practice Area */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Practice Area</label>
            <SearchableSelect
              options={PRACTICE_AREAS}
              value={data.practiceArea || null}
              onChange={(val) => onChange({ practiceArea: val || "other" })}
              placeholder="Select practice area"
            />
          </div>

          {/* Billing section header */}
          <div className="flex items-center gap-3 pt-1">
            <DollarSign className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Billing</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          {/* Billing Type pills */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Billing Type</label>
            <div className="flex gap-2">
              {BILLING_TYPES.map((bt) => (
                <button
                  key={bt.value}
                  type="button"
                  onClick={() => onChange({ billingType: bt.value })}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    data.billingType === bt.value
                      ? "text-white shadow-sm"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  style={data.billingType === bt.value ? { backgroundColor: "var(--color-primary)" } : {}}
                >
                  {bt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Flat Fee amount */}
          {data.billingType === "flat" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Flat Fee Amount ($)</label>
              <input
                type="number"
                value={data.flatFeeCents}
                onChange={(e) => onChange({ flatFeeCents: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="0.00"
                min={0}
                step="0.01"
              />
            </div>
          )}

          {/* Rate Table for hourly */}
          {data.billingType === "hourly" && rateTables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rate Table</label>
              <SearchableSelect
                options={rateTables.map((rt) => ({ value: rt._id, label: rt.name }))}
                value={data.rateTableId}
                onChange={(val) => onChange({ rateTableId: val })}
                placeholder="Select rate table (optional)"
                clearable
              />
            </div>
          )}

          {/* Team section header */}
          <div className="flex items-center gap-3 pt-1">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Team</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          {/* Responsible Team Member */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Responsible Team Member <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={teamUsers.map((u) => ({ value: u._id, label: u.name, description: u.email }))}
              value={data.responsibleLawyerId || null}
              onChange={(val) => onChange({ responsibleLawyerId: val || "" })}
              placeholder="Select team member"
            />
            {errors.responsibleLawyerId && <p className="text-xs text-red-500 mt-1">{errors.responsibleLawyerId}</p>}
          </div>

          {/* Conflict check same as client */}
          <div className="pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.matterConflictSameAsClient}
                onChange={(e) => onChange({ matterConflictSameAsClient: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700">Use same conflict check as client</span>
            </label>
          </div>

          {/* Separate matter conflict check */}
          {!data.matterConflictSameAsClient && (
            <div className="space-y-3 pl-6 border-l-2 border-slate-200">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Matter Conflict Status</label>
                <select
                  value={data.matterConflictStatus}
                  onChange={(e) => onChange({ matterConflictStatus: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 bg-white"
                >
                  <option value="not_started">Not Started</option>
                  <option value="passed">Passed</option>
                  <option value="waived">Waived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={data.matterConflictNotes}
                  onChange={(e) => onChange({ matterConflictNotes: e.target.value })}
                  rows={2}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-none"
                  placeholder="Conflict check notes..."
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatterSetupStep;
